import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  HeartHandshake, Loader2, Bot, Layers, Plus, CalendarClock,
  FileText, MessageSquareText, ChevronDown, ChevronUp,
  Info, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACP_NAV = [
  { icon: Layers, label: 'Planner Dashboard', href: '/planner/dashboard' },
  { icon: Bot, label: 'Orchestrator', href: '/planner/orchestrator' },
  { icon: HeartHandshake, label: 'Advance Care Planning', active: true },
];

const DIRECTIVE_TYPES = [
  { value: 'polst', label: 'POLST' },
  { value: 'molst', label: 'MOLST' },
  { value: 'living_will', label: 'Living Will' },
  { value: 'healthcare_proxy', label: 'Healthcare Proxy' },
  { value: 'dnr', label: 'DNR' },
  { value: 'other', label: 'Other' },
];
const DIRECTIVE_STATUSES = ['discussed', 'documented', 'signed', 'on_file'];
const STATUS_TONE = {
  discussed: 'bg-slate-100 text-slate-700 border-slate-200',
  documented: 'bg-sky-50 text-sky-700 border-sky-200',
  signed: 'bg-amber-50 text-amber-700 border-amber-200',
  on_file: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function AdvanceCarePlanning() {
  const { getAuthHeader } = useAuth();
  const [records, setRecords] = useState([]);
  const [due, setDue] = useState([]);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [newPatientId, setNewPatientId] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/acp/records`, getAuthHeader()),
        axios.get(`${API}/acp/follow-ups/due`, getAuthHeader()),
      ]);
      setRecords(r1.data.rows || []);
      setDue(r2.data.rows || []);
      setNotice(r1.data.decision_support_notice || '');
      setGated(false);
    } catch (e) {
      if (e.response?.status === 402) setGated(true);
      else toast.error('Could not load ACP records');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const createRecord = async () => {
    if (!newPatientId.trim()) { toast.error('Patient ID required'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/acp/records`,
        { patient_id: newPatientId.trim() }, getAuthHeader());
      toast.success(r.data.created ? 'ACP record created' : 'Record already exists — opened');
      setNewPatientId('');
      setExpanded(r.data.record.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not create record');
    } finally { setCreating(false); }
  };

  if (gated) {
    return (
      <DashboardShell navItems={ACP_NAV} title="Advance Care Planning"
        subtitle="Not enabled for your organization." testId="acp-page">
        <div className="cn-card p-8 text-center" data-testid="acp-flag-off">
          <HeartHandshake className="w-10 h-10 mx-auto text-slate-400 mb-3" />
          <div className="cn-h3 mb-2">ACP module not enabled</div>
          <p className="text-cn-text-muted text-sm">
            Ask your CaraLoom admin to flip the <code>advance_care_planning</code> entitlement
            flag for your organization.
          </p>
        </div>
      </DashboardShell>
    );
  }

  if (loading) {
    return (
      <DashboardShell navItems={ACP_NAV} title="Advance Care Planning"
        subtitle="Directive tracking + goals-of-care conversations." testId="acp-page">
        <div className="cn-card p-10 text-center" data-testid="acp-loading">
          <Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" />
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell navItems={ACP_NAV} title="Advance Care Planning"
      subtitle="Track directives, log goals-of-care conversations, never miss a re-discussion."
      testId="acp-page">

      {/* Decision-support boundary */}
      <div className="flex items-start gap-2 border border-sky-200 bg-sky-50/60 rounded px-3 py-2 mb-5 text-xs text-sky-900"
        data-testid="acp-disclaimer">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{notice || 'Decision-support documentation only — CaraLoom does not provide medical or legal advice.'}</span>
      </div>

      {/* Due follow-ups */}
      {due.length > 0 && (
        <div className="cn-card p-4 mb-6" data-testid="acp-due-followups">
          <div className="flex items-center gap-2 font-semibold mb-3">
            <CalendarClock className="w-4 h-4 text-red-600" />
            <span>Follow-ups due</span>
          </div>
          <div className="space-y-2">
            {due.map((r) => (
              <div key={r.id}
                className={`flex items-center justify-between rounded px-3 py-2 border ${r.overdue ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40'}`}
                data-testid={`acp-due-${r.id}`}>
                <div className="text-sm">
                  <span className="font-medium">Patient <code className="text-xs">{r.patient_id?.slice(0, 14)}…</code></span>
                  <span className="text-xs text-cn-text-muted ml-2">
                    {r.overdue ? 'OVERDUE' : 'due'} · {(r.follow_up?.next_review_at || '').slice(0, 10)}
                    {r.follow_up?.reason ? ` — ${r.follow_up.reason}` : ''}
                  </span>
                </div>
                <Button onClick={() => setExpanded(r.id)}
                  className="cn-btn cn-btn-ghost !py-1 !text-xs"
                  data-testid={`acp-due-open-${r.id}`}>
                  Open
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New record */}
      <div className="cn-card p-4 mb-6" data-testid="acp-create-card">
        <div className="flex items-center gap-2 font-semibold mb-3">
          <Plus className="w-4 h-4 text-cn-primary" />
          <span>Start an ACP record</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input value={newPatientId} onChange={(e) => setNewPatientId(e.target.value)}
            placeholder="Patient ID" className="max-w-xs"
            data-testid="acp-new-patient-id" />
          <Button onClick={createRecord} disabled={creating}
            className="cn-btn cn-btn-primary" data-testid="acp-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create record
          </Button>
        </div>
      </div>

      {/* Records list */}
      <div className="cn-card p-4" data-testid="acp-records">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-semibold">
            <FileText className="w-4 h-4 text-cn-primary" />
            <span>ACP records ({records.length})</span>
          </div>
          <Button onClick={load} className="cn-btn cn-btn-ghost !py-1 !text-xs" data-testid="acp-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
        {records.length === 0 ? (
          <div className="text-center py-8 text-sm text-cn-text-muted" data-testid="acp-empty">
            No ACP records yet. Start one above.
          </div>
        ) : (
          <div className="space-y-2">
            {records.map((r) => (
              <RecordRow key={r.id} record={r}
                open={expanded === r.id}
                onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function RecordRow({ record, open, onToggle, onChanged }) {
  const dirCount = record.directives?.length || 0;
  const convoCount = record.goc_conversations?.length || 0;
  return (
    <div className="border border-slate-200 rounded" data-testid={`acp-record-${record.id}`}>
      <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50"
        onClick={onToggle} data-testid={`acp-record-toggle-${record.id}`}>
        <div className="text-sm flex items-center gap-2 flex-wrap">
          <span className="font-medium">Patient <code className="text-xs">{record.patient_id?.slice(0, 16)}…</code></span>
          <span className="text-[0.65rem] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-200">
            {dirCount} directive{dirCount === 1 ? '' : 's'}
          </span>
          <span className="text-[0.65rem] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-200">
            {convoCount} GOC conversation{convoCount === 1 ? '' : 's'}
          </span>
          {record.follow_up?.next_review_at && (
            <span className="text-[0.65rem] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
              review {(record.follow_up.next_review_at || '').slice(0, 10)}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>
      {open && <RecordDetail record={record} onChanged={onChanged} />}
    </div>
  );
}

function RecordDetail({ record, onChanged }) {
  const { getAuthHeader } = useAuth();
  const [tab, setTab] = useState(null); // 'directive' | 'conversation' | 'followup'
  const [busy, setBusy] = useState(false);

  const post = async (path, body, method = 'post') => {
    setBusy(true);
    try {
      await axios[method](`${API}/acp/records/${record.id}${path}`, body, getAuthHeader());
      toast.success('Saved');
      setTab(null);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not save'));
    } finally { setBusy(false); }
  };

  const updateDirectiveStatus = async (directiveId, status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/acp/records/${record.id}/directives/${directiveId}`,
        { status }, getAuthHeader());
      toast.success(`Directive → ${status.replace('_', ' ')}`);
      await onChanged();
    } catch (e) {
      toast.error('Could not update directive');
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-slate-100 px-3 py-3 space-y-4" data-testid={`acp-detail-${record.id}`}>
      {/* Directives */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-cn-text-muted mb-2">Directives</div>
        {(record.directives || []).length === 0 ? (
          <div className="text-xs text-cn-text-muted">None on file yet.</div>
        ) : (
          <div className="space-y-1.5">
            {record.directives.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 text-sm flex-wrap"
                data-testid={`acp-directive-${d.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{(DIRECTIVE_TYPES.find((t) => t.value === d.directive_type) || {}).label || d.directive_type}</span>
                  <span className={`text-[0.65rem] px-1.5 py-0.5 rounded border ${STATUS_TONE[d.status] || STATUS_TONE.discussed}`}>
                    {d.status.replace('_', ' ')}
                  </span>
                  {d.notes && <span className="text-xs text-cn-text-muted">{d.notes}</span>}
                </div>
                <select value={d.status} disabled={busy}
                  onChange={(e) => updateDirectiveStatus(d.id, e.target.value)}
                  className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                  data-testid={`acp-directive-status-${d.id}`}>
                  {DIRECTIVE_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Conversations */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-cn-text-muted mb-2">Goals-of-care conversations</div>
        {(record.goc_conversations || []).length === 0 ? (
          <div className="text-xs text-cn-text-muted">No conversations logged.</div>
        ) : (
          <div className="space-y-2">
            {record.goc_conversations.map((c) => (
              <div key={c.id} className="border border-slate-100 rounded px-2.5 py-2 text-xs bg-slate-50/50"
                data-testid={`acp-convo-${c.id}`}>
                <div className="flex items-center gap-2 flex-wrap font-medium text-sm">
                  <MessageSquareText className="w-3.5 h-3.5 text-cn-primary" />
                  {(c.occurred_at || '').slice(0, 10)}
                  {c.setting && <span className="text-[0.65rem] px-1.5 py-0.5 rounded border bg-white border-slate-200 font-normal">{c.setting}</span>}
                </div>
                <div className="mt-1 text-cn-text-muted">
                  {(c.participants || []).map((p) => `${p.name} (${p.role})`).join(' · ')}
                </div>
                {(c.topics || []).length > 0 && (
                  <div className="mt-1">Topics: {c.topics.join(', ')}</div>
                )}
                <PrefsLine prefs={c.documented_preferences} />
                {c.outcome && <div className="mt-1 italic">{c.outcome}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => setTab(tab === 'directive' ? null : 'directive')}
          className="cn-btn cn-btn-ghost !py-1 !text-xs" data-testid={`acp-add-directive-btn-${record.id}`}>
          <Plus className="w-3 h-3" /> Add directive
        </Button>
        <Button onClick={() => setTab(tab === 'conversation' ? null : 'conversation')}
          className="cn-btn cn-btn-ghost !py-1 !text-xs" data-testid={`acp-log-convo-btn-${record.id}`}>
          <MessageSquareText className="w-3 h-3" /> Log GOC conversation
        </Button>
        <Button onClick={() => setTab(tab === 'followup' ? null : 'followup')}
          className="cn-btn cn-btn-ghost !py-1 !text-xs" data-testid={`acp-set-followup-btn-${record.id}`}>
          <CalendarClock className="w-3 h-3" /> Set follow-up
        </Button>
      </div>

      {tab === 'directive' && <DirectiveForm busy={busy} onSubmit={(b) => post('/directives', b)} />}
      {tab === 'conversation' && <ConversationForm busy={busy} onSubmit={(b) => post('/conversations', b)} />}
      {tab === 'followup' && <FollowUpForm busy={busy} onSubmit={(b) => post('/follow-up', b)} />}
    </div>
  );
}

function PrefsLine({ prefs }) {
  if (!prefs) return null;
  const bits = [];
  if (prefs.code_status) bits.push(`code status: ${prefs.code_status.replace('_', ' ')}`);
  if (prefs.treatment_intensity) bits.push(`intensity: ${prefs.treatment_intensity.replace(/_/g, ' ')}`);
  if (prefs.hospitalization_preference) bits.push(`hospitalization: ${prefs.hospitalization_preference.replace(/_/g, ' ')}`);
  if (bits.length === 0 && !prefs.other_notes) return null;
  return (
    <div className="mt-1">
      <span className="font-medium">Documented preferences:</span> {bits.join(' · ')}
      {prefs.other_notes && <span> — {prefs.other_notes}</span>}
    </div>
  );
}

function DirectiveForm({ busy, onSubmit }) {
  const [type, setType] = useState('polst');
  const [status, setStatus] = useState('discussed');
  const [notes, setNotes] = useState('');
  return (
    <div className="border border-slate-200 rounded p-3 space-y-2" data-testid="acp-directive-form">
      <div className="flex gap-2 flex-wrap">
        <select value={type} onChange={(e) => setType(e.target.value)}
          className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
          data-testid="acp-directive-type-select">
          {DIRECTIVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
          data-testid="acp-directive-status-select">
          {DIRECTIVE_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
        data-testid="acp-directive-notes" />
      <Button disabled={busy}
        onClick={() => onSubmit({ directive_type: type, status, notes: notes || null })}
        className="cn-btn cn-btn-primary !py-1 !text-xs" data-testid="acp-directive-save">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save directive
      </Button>
    </div>
  );
}

function ConversationForm({ busy, onSubmit }) {
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 10));
  const [setting, setSetting] = useState('home');
  const [participants, setParticipants] = useState([{ name: '', role: 'patient' }]);
  const [topics, setTopics] = useState('');
  const [codeStatus, setCodeStatus] = useState('');
  const [intensity, setIntensity] = useState('');
  const [hosp, setHosp] = useState('');
  const [outcome, setOutcome] = useState('');
  const [followDays, setFollowDays] = useState('');

  const setP = (i, field, v) => {
    const next = participants.slice();
    next[i] = { ...next[i], [field]: v };
    setParticipants(next);
  };

  const submit = () => {
    const parts = participants.filter((p) => p.name.trim());
    if (parts.length === 0) { toast.error('Add at least one participant'); return; }
    onSubmit({
      occurred_at: occurredAt,
      setting,
      participants: parts,
      topics: topics.split(',').map((t) => t.trim()).filter(Boolean),
      documented_preferences: {
        code_status: codeStatus || null,
        treatment_intensity: intensity || null,
        hospitalization_preference: hosp || null,
      },
      outcome: outcome || null,
      follow_up_in_days: followDays ? Number(followDays) : null,
    });
  };

  return (
    <div className="border border-slate-200 rounded p-3 space-y-2" data-testid="acp-convo-form">
      <div className="flex gap-2 flex-wrap">
        <Input type="date" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)}
          className="max-w-[160px]" data-testid="acp-convo-date" />
        <select value={setting} onChange={(e) => setSetting(e.target.value)}
          className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
          data-testid="acp-convo-setting">
          {['home', 'clinic', 'hospital', 'telehealth', 'phone'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        {participants.map((p, i) => (
          <div key={i} className="flex gap-2">
            <Input value={p.name} onChange={(e) => setP(i, 'name', e.target.value)}
              placeholder="Participant name" className="max-w-xs"
              data-testid={`acp-convo-participant-name-${i}`} />
            <select value={p.role} onChange={(e) => setP(i, 'role', e.target.value)}
              className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
              data-testid={`acp-convo-participant-role-${i}`}>
              {['patient', 'surrogate', 'clinician', 'family', 'other'].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        ))}
        <Button onClick={() => setParticipants([...participants, { name: '', role: 'family' }])}
          className="cn-btn cn-btn-ghost !py-1 !text-xs" data-testid="acp-convo-add-participant">
          <Plus className="w-3 h-3" /> Add participant
        </Button>
      </div>
      <Input value={topics} onChange={(e) => setTopics(e.target.value)}
        placeholder="Topics (comma-separated: code status, hospitalization…)"
        data-testid="acp-convo-topics" />
      <div className="grid sm:grid-cols-3 gap-2">
        <select value={codeStatus} onChange={(e) => setCodeStatus(e.target.value)}
          className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
          data-testid="acp-convo-code-status">
          <option value="">Code status…</option>
          {['full_code', 'dnr', 'dni', 'comfort_care', 'undecided'].map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={intensity} onChange={(e) => setIntensity(e.target.value)}
          className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
          data-testid="acp-convo-intensity">
          <option value="">Treatment intensity…</option>
          {['full_treatment', 'selective', 'comfort_focused', 'undecided'].map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={hosp} onChange={(e) => setHosp(e.target.value)}
          className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
          data-testid="acp-convo-hosp-pref">
          <option value="">Hospitalization…</option>
          {['hospitalize', 'avoid_hospitalization', 'case_by_case', 'undecided'].map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>
      <Textarea value={outcome} onChange={(e) => setOutcome(e.target.value)}
        placeholder="Outcome / what was decided (documented verbatim — no advice)"
        data-testid="acp-convo-outcome" />
      <div className="flex items-center gap-2">
        <Input type="number" min="1" max="365" value={followDays}
          onChange={(e) => setFollowDays(e.target.value)}
          placeholder="Re-discuss in (days)" className="max-w-[180px]"
          data-testid="acp-convo-followup-days" />
        <Button disabled={busy} onClick={submit}
          className="cn-btn cn-btn-primary !py-1 !text-xs" data-testid="acp-convo-save">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Log conversation
        </Button>
      </div>
    </div>
  );
}

function FollowUpForm({ busy, onSubmit }) {
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  return (
    <div className="border border-slate-200 rounded p-3 space-y-2" data-testid="acp-followup-form">
      <div className="flex gap-2 flex-wrap">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="max-w-[160px]" data-testid="acp-followup-date" />
        <Input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (status change, readmission…)" className="max-w-sm"
          data-testid="acp-followup-reason" />
        <Button disabled={busy || !date}
          onClick={() => onSubmit({ next_review_at: `${date}T00:00:00+00:00`, reason: reason || null })}
          className="cn-btn cn-btn-primary !py-1 !text-xs" data-testid="acp-followup-save">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Set follow-up
        </Button>
      </div>
    </div>
  );
}
